import { getSupabase } from "@/integrations/supabase/client";
import { DogSchool } from "@/data/dogSchools";
import { calculateDistance, getDefaultCoordinates } from "./coordinate-calculation";
import { mapSupabaseRowToDogSchool } from "@/utils/supabaseMappers";

// Cache für Koordinaten - DEAKTIVIERT, um immer frische Daten zu verwenden
// Commenting out these caches as per request to always fetch from Supabase directly
// const postalCodeCache = new Map<string, {lat: number, lng: number} | null>();
// const cityCache = new Map<string, {lat: number, lng: number} | null>();
// const cityToPostalCodeCache = new Map<string, string | null>();

/**
 * Fetch coordinates from hundeschuleList table by postal code
 */
export const getCoordinatesByPostalCode = async (postalCode: string): Promise<{lat: number, lng: number} | null> => {
  try {
    // Don't check cache, always fetch fresh data
    console.log(`[Coordinate Lookup] Starting coordinate lookup for postal code: ${postalCode}`);
    const supabase = getSupabase();
    
    // Debug: Check schools in the area
    console.log('[Debug] Starting database query for schools in area...');
    
    // First, check if schools exist at all
    const { data: allSchoolsCheck, error: allSchoolsError } = await supabase
      .from('hundeschuleList')
      .select('postal_code, city, latitude, longitude')
      .in('postal_code', ['77723', '77749', '77791']);
      
    if (allSchoolsError) {
      console.error('[Debug] Error checking all schools:', allSchoolsError);
    } else {
      console.log('[Debug] All schools in target area:', JSON.stringify(allSchoolsCheck, null, 2));
    }
    
    // Then check schools with coordinates
    const { data: schoolsWithCoords, error: coordsError } = await supabase
      .from('hundeschuleList')
      .select('postal_code, city, latitude, longitude')
      .in('postal_code', ['77723', '77749', '77791'])
      .not('latitude', 'is', null)
      .not('longitude', 'is', null);
      
    if (coordsError) {
      console.error('[Debug] Error checking schools with coordinates:', coordsError);
    } else {
      console.log('[Debug] Schools with coordinates in target area:', JSON.stringify(schoolsWithCoords, null, 2));
    }
    
    // PRIORITÄT 1: Try to find postal code in postal_code_coordinates table first
    console.log(`[Coordinate Lookup] Attempting to find coordinates in postal_code_coordinates table for ${postalCode}`);
    const { data: postalCoordData, error: postalCoordError } = await supabase
      .from('postal_code_coordinates')
      .select('latitude, longitude')
      .eq('postal_code', postalCode)
      .limit(1);
      
    if (postalCoordError) {
      console.log(`[Coordinate Lookup] Error querying postal_code_coordinates table:`, postalCoordError);
    }
      
    if (postalCoordData && postalCoordData.length > 0) {
      console.log(`[Coordinate Lookup] Successfully found coordinates in postal_code_coordinates table for ${postalCode}: ${postalCoordData[0].latitude}, ${postalCoordData[0].longitude}`);
      return { 
        lat: parseFloat(String(postalCoordData[0].latitude)), 
        lng: parseFloat(String(postalCoordData[0].longitude)) 
      };
    } else {
      console.log(`[Coordinate Lookup] No coordinates found in postal_code_coordinates table for ${postalCode}`);
    }
    
    // PRIORITÄT 2: Fallback to exact match on postal_code in hundeschuleList
    console.log(`[Coordinate Lookup] Falling back to hundeschuleList table for ${postalCode}`);
    const { data: exactMatchData, error: exactMatchError } = await supabase
      .from('hundeschuleList')
      .select('latitude, longitude, postal_code')
      .eq('postal_code', postalCode)
      .not('latitude', 'is', null)
      .not('longitude', 'is', null)
      .limit(1);
      
    if (exactMatchError) {
      console.log(`[Coordinate Lookup] Error querying hundeschuleList table:`, exactMatchError);
    }
      
    if (exactMatchData && exactMatchData.length > 0) {
      console.log(`[Coordinate Lookup] Successfully found coordinates in hundeschuleList for ${postalCode}: ${exactMatchData[0].latitude}, ${exactMatchData[0].longitude}`);
      return { 
        lat: parseFloat(String(exactMatchData[0].latitude)), 
        lng: parseFloat(String(exactMatchData[0].longitude)) 
      };
    } else {
      console.log(`[Coordinate Lookup] No coordinates found in hundeschuleList table for ${postalCode}`);
    }
    
    console.log(`[Coordinate Lookup] No coordinates found for postal code: ${postalCode} after trying all lookup methods`);
    return null;
  } catch (error) {
    console.error("[Coordinate Lookup] Error in getCoordinatesByPostalCode:", error);
    return null;
  }
};

/**
 * Get postal code by city name from hundeschuleList table
 */
export const getPostalCodeByCity = async (city: string): Promise<string | null> => {
  try {
    // Don't check cache, always fetch fresh data
    console.log(`Looking up postal code for city: ${city}`);
    const supabase = getSupabase();
    
    // Try to find postal code in the hundeschuleList table
    let query = supabase
      .from('hundeschuleList')
      .select('postal_code')
      .ilike('city', `%${city}%`)
      .not('postal_code', 'is', null)
      .limit(1);
      
    // Check if this is a known German city
    if (isGermanCity(city)) {
      console.log(`Detected German city: ${city}, filtering for Germany`);
      query = query.ilike('country', '%Germany%');
    }
    
    const { data, error } = await query;
    
    if (error || !data || data.length === 0) {
      console.log(`No postal code found for city ${city}`);
      return null;
    }
    
    console.log(`Found postal code ${data[0].postal_code} for city ${city}`);
    return data[0].postal_code;
  } catch (error) {
    console.error("Error getting postal code by city:", error);
    return null;
  }
};

/**
 * Fetch coordinates by city name from the hundeschuleList table
 */
export const getCoordinatesByCity = async (city: string): Promise<{lat: number, lng: number} | null> => {
  try {
    // Don't check cache, always fetch fresh data
    console.log(`Fetching coordinates for city: ${city}`);
    const supabase = getSupabase();
    
    // Try to get coordinates from hundeschuleList table where city matches
    let query = supabase
      .from('hundeschuleList')
      .select('latitude, longitude')
      .ilike('city', `%${city}%`)
      .not('latitude', 'is', null)
      .not('longitude', 'is', null)
      .limit(1);
      
    // Check if this is a known German city
    if (isGermanCity(city)) {
      console.log(`Detected German city: ${city}, filtering for Germany`);
      query = query.ilike('country', '%Germany%');
    }
    
    const { data, error } = await query;
    
    if (error || !data || data.length === 0) {
      console.log(`No coordinates found for city ${city} in hundeschuleList`);
      return null;
    }
    
    console.log(`Found coordinates for city ${city}: ${data[0].latitude}, ${data[0].longitude}`);
    return { 
      lat: typeof data[0].latitude === 'number' ? data[0].latitude : parseFloat(String(data[0].latitude)),
      lng: typeof data[0].longitude === 'number' ? data[0].longitude : parseFloat(String(data[0].longitude))
    };
  } catch (error) {
    console.error("Error getting coordinates by city:", error);
    return null;
  }
};

/**
 * Check if this is a known German city
 */
const isGermanCity = (city: string): boolean => {
  const germanCities = [
    'berlin', 'hamburg', 'münchen', 'munich', 'köln', 'cologne', 'koln',
    'frankfurt', 'stuttgart', 'düsseldorf', 'dusseldorf', 'leipzig',
    'dortmund', 'essen', 'bremen', 'dresden', 'hannover', 'nürnberg',
    'nuremberg', 'nurnberg', 'duisburg', 'bochum', 'wuppertal', 'bielefeld',
    'bonn', 'mannheim', 'karlsruhe', 'augsburg', 'wiesbaden', 'münster',
    'munster', 'gelsenkirchen', 'aachen', 'mönchengladbach', 'braunschweig'
  ];
  
  return germanCities.some(germanCity => 
    city.toLowerCase().includes(germanCity)
  );
};

// Keep cache clearing functions for testing, but they're not used in main flow anymore
export const clearCoordinatesCaches = () => {
  // Cache is disabled, nothing to clear
  console.log('Cache is disabled, no need to clear');
};

export async function fetchDogSchoolsByPostalCode(
  postalCode: string,
  city?: string,
  radius: number = 50,
  country: string = 'DE'
): Promise<DogSchool[]> {
  try {
    // Start with a very visible log
    console.log('🔍 ====== STARTING SCHOOL SEARCH ====== 🔍');
    console.log('Search parameters:', { postalCode, city, country, radius });
    
    // Get coordinates for the search location
    let searchCoordinates;
    
    if (postalCode) {
      searchCoordinates = await getCoordinatesByPostalCode(postalCode);
      console.log('📍 Search coordinates:', searchCoordinates);
    }
    
    if (!searchCoordinates && city) {
      searchCoordinates = await getCoordinatesByCity(city);
      console.log('📍 Search coordinates from city:', searchCoordinates);
    }
    
    if (!searchCoordinates) {
      const countryName = country === 'DE' ? 'Germany' : 
                         country === 'AT' ? 'Austria' : 
                         country === 'CH' ? 'Switzerland' : 'Germany';
      
      searchCoordinates = getDefaultCoordinates(countryName);
      console.log('⚠️ Using default country coordinates:', searchCoordinates);
    }
    
    console.log(`🔍 Searching for schools within ${radius}km radius of coordinates:`, searchCoordinates);
    
    // Fetch all schools with coordinates from database
    console.log('📊 Fetching schools from database...');
    const { data: allSchools, error: dbError } = await getSupabase()
      .from('hundeschuleList')
      .select('*')
      .not('latitude', 'is', null)
      .not('longitude', 'is', null);
    
    if (dbError) {
      console.error('❌ Database query error:', dbError);
      return [];
    }
    
    if (!allSchools || allSchools.length === 0) {
      console.log('⚠️ No schools found in database with valid coordinates');
      return [];
    }
    
    console.log(`✅ Found ${allSchools.length} schools with coordinates in database`);
    
    // Log sample of schools with their coordinates
    console.log('📋 Sample of schools with coordinates:');
    allSchools.slice(0, 3).forEach(school => {
      console.log(`   • ${school.name} (${school.postal_code}): [${school.latitude}, ${school.longitude}]`);
    });
    
    // Convert to DogSchool objects and calculate distances
    console.log('📏 Calculating distances for all schools...');
    const schoolsWithDistance = allSchools.map(school => {
      try {
        const dogSchool = mapSupabaseRowToDogSchool(school);
        if (!dogSchool) return null;
        
        const distanceKm = calculateDistance(
          searchCoordinates.lat,
          searchCoordinates.lng,
          dogSchool.latitude || 0,
          dogSchool.longitude || 0
        );
        
        return {
          ...dogSchool,
          distance: distanceKm,
          location: {
            lat: dogSchool.latitude || 0,
            lng: dogSchool.longitude || 0
          }
        };
      } catch (error) {
        console.error('❌ Error mapping school:', error, school);
        return null;
      }
    }).filter((school): school is (DogSchool & { distance: number; location: { lat: number; lng: number } }) => 
      school !== null && typeof (school as any).distance === 'number'
    );
    
    console.log(`✅ Calculated distances for ${schoolsWithDistance.length} schools`);
    
    // Filter schools by distance with detailed logging
    console.log(`🔍 Filtering schools within ${radius}km radius...`);
    const schoolsInRadius = schoolsWithDistance.filter(school => {
      const withinRadius = school.distance <= radius;
      if (withinRadius) {
        console.log(`   • ${school.name} (${school.postalCode}): ${school.distance.toFixed(2)}km`);
      }
      return withinRadius;
    });
    
    console.log(`✅ Found ${schoolsInRadius.length} schools within ${radius}km radius`);
    
    // Sort by distance
    const sortedSchools = schoolsInRadius.sort((a, b) => {
      return a.distance - b.distance;
    });
    
    // Final results with detailed logging
    console.log('📋 Final sorted results:');
    sortedSchools.forEach((school, index) => {
      console.log(`   ${index + 1}. ${school.name} (${school.postalCode}): ${school.distance.toFixed(2)}km`);
    });
    
    console.log('🔍 ====== SEARCH COMPLETE ====== 🔍');
    return sortedSchools;
    
  } catch (error) {
    console.error('❌ Error in fetchDogSchoolsByPostalCode:', error);
    return [];
  }
}
